import { useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CrmError } from './CrmError';

export function CrmFileCard({ children }: { children: ReactNode }) {
  const { t } = useTranslation('member');
  const root = useRef<HTMLDivElement>(null);
  const [selected, setSelected] = useState(false);
  return (
    <div
      ref={root}
      onChangeCapture={() =>
        setSelected(
          !!root.current?.querySelector<HTMLInputElement>('input[type=file]')?.files?.length,
        )
      }
      className="min-w-0 border border-base-300 bg-base-100 p-4 space-y-2 [&_input]:max-w-full [&_input]:min-h-11"
    >
      {children}
      <p className="text-sm text-base-content/70">{t('crmWorkspace.fileLimit')}</p>
      {selected && (
        <button
          type="button"
          className="min-h-11 border border-base-300 px-3 text-sm"
          onClick={() => {
            const input = root.current?.querySelector<HTMLInputElement>('input[type=file]');
            if (!input || input.disabled || input.matches(':disabled')) return;
            input.value = '';
            // Use the adapter's existing change handler so files, decisions and freshness clear together.
            input.dispatchEvent(new Event('change', { bubbles: true }));
            setSelected(false);
            input.focus();
          }}
        >
          {t('crmWorkspace.removeFiles')}
        </button>
      )}
    </div>
  );
}
export function CrmSummary({
  rows,
  issues,
  ready,
  facts = [],
}: {
  rows: number;
  issues: number;
  ready: boolean;
  facts?: { label: string; value: ReactNode }[];
}) {
  const { t } = useTranslation('member');
  return (
    <section aria-label={t('crmWorkspace.summary')} className="border border-base-300 p-4">
      <h3 className="font-semibold mb-3">{t('crmWorkspace.summary')}</h3>
      <dl className="grid gap-4 sm:grid-cols-3">
        <div>
          <dt>{t('crmWorkspace.rows')}</dt>
          <dd className="text-xl tabular-nums">{rows}</dd>
        </div>
        <div>
          <dt>{t('crmWorkspace.issues')}</dt>
          <dd className="text-xl tabular-nums">{issues}</dd>
        </div>
        <div>
          <dt>{t('crmWorkspace.readiness')}</dt>
          <dd>{t(ready ? 'crmWorkspace.ready' : 'crmWorkspace.resolve')}</dd>
        </div>
        {facts.map((fact, i) => (
          <div key={i}>
            <dt>{fact.label}</dt>
            <dd className="text-xl tabular-nums break-words">{fact.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
export function CrmResult({ children, receipt }: { children: ReactNode; receipt?: ReactNode }) {
  const { t } = useTranslation('member');
  return (
    <section role="status" className="border border-success p-4 space-y-3">
      <h3 className="font-semibold">{t('crmWorkspace.RESULT')}</h3>
      {children}
      {receipt && (
        <details>
          <summary className="min-h-11 cursor-pointer">{t('crmErrors.details')}</summary>
          <div className="break-all text-sm">{receipt}</div>
        </details>
      )}
    </section>
  );
}

export interface CrmRowFilter {
  venue: string;
  blockedOnly: boolean;
}
export function matchesCrmFilter(
  row: { venueId?: string | null; holds: readonly string[] },
  filter: CrmRowFilter,
) {
  return (
    (!filter.venue || row.venueId === filter.venue) && (!filter.blockedOnly || row.holds.length > 0)
  );
}
export function CrmRowFilters({
  value,
  onChange,
  venues,
  total,
  matched,
  limit,
  onMore,
}: {
  value: CrmRowFilter;
  onChange: (value: CrmRowFilter) => void;
  venues: { id: string; name: string }[];
  total: number;
  matched: number;
  limit: number;
  onMore: () => void;
}) {
  const { t } = useTranslation('member');
  return (
    <section className="border border-base-300 p-4 space-y-3" aria-label={t('crmWorkspace.filter')}>
      <div className="flex flex-wrap gap-3">
        <label className="min-w-0 flex-1">
          {t('opening.destination')}
          <select
            className="h-11 w-full border border-base-300 bg-base-100 px-3"
            value={value.venue}
            onChange={(e) => onChange({ ...value, venue: e.target.value })}
          >
            <option value="">{t('crmWorkspace.allVenues')}</option>
            {venues.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex min-h-11 items-center gap-2">
          <input
            type="checkbox"
            checked={value.blockedOnly}
            onChange={(e) => onChange({ ...value, blockedOnly: e.target.checked })}
          />
          {t('crmWorkspace.blockedOnly')}
        </label>
      </div>
      <p className="text-sm">
        {t('crmWorkspace.filterCounts', { shown: Math.min(matched, limit), matched, total })}
      </p>
      {matched > limit && (
        <button type="button" className="min-h-11 border border-base-300 px-4" onClick={onMore}>
          {t('crmWorkspace.moreRows')}
        </button>
      )}
    </section>
  );
}

/** Adapter-supplied values stay in their original units; this component never totals money. */
export function CrmVenueImpact({
  columns,
  rows,
  children,
}: {
  columns: string[];
  rows: { key: string; cells: ReactNode[] }[];
  children?: ReactNode;
}) {
  const { t } = useTranslation('member');
  return (
    <section className="min-w-0 space-y-3" aria-label={t('opening.venueTotals')}>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm [&_th]:p-2 [&_td]:p-2">
          <caption className="p-2 text-left font-semibold">{t('opening.venueTotals')}</caption>
          <thead>
            <tr>
              {columns.map((label, i) => (
                <th key={i} scope="col">
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key} className="border-b border-base-300">
                {row.cells.map((cell, i) => (
                  <td key={i} className={i ? 'text-right tabular-nums' : ''}>
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {children}
    </section>
  );
}
export function CrmIssues({ codes }: { codes: string[] }) {
  const { t } = useTranslation('member');
  const unique = [...new Set(codes)];
  return unique.length ? (
    <section aria-label={t('crmWorkspace.issues')} className="border border-warning p-4 space-y-2">
      <h3 className="font-semibold">{t('crmWorkspace.issues')}</h3>
      <ul className="list-disc pl-5 space-y-2">
        {unique.map((code) => (
          <li key={code}>
            <CrmError error={code} />
          </li>
        ))}
      </ul>
    </section>
  ) : null;
}
